import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { clearApplySession } from '../../lib/screeningApplySession';
import {
  CheckCircleIcon,
  XMarkIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useCandidateLanguage } from '../../i18n/candidate';

/**
 * Common Thank You page after completing the screening process.
 * Works for: Assessment only, Interview only, or both.
 * Disables back navigation to screening steps.
 * Optional feedback: 5-star rating + text, saved to candidate record.
 */
const ScreeningThankYou = () => {
  const { screeningId } = useParams();
  const t = useCandidateLanguage({ screeningId });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const emailFromUrl = searchParams.get('email') || '';
  const emailFromState = location.state?.email || location.state?.candidateInfo?.email || '';
  const candidateEmail = (emailFromUrl || emailFromState || '').trim();

  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const canSubmitFeedback = candidateEmail && stars >= 1;

  useEffect(() => {
    if (screeningId) clearApplySession(screeningId);
  }, [screeningId]);

  // Prevent back navigation to screening steps (hub, assessment, interview)
  useEffect(() => {
    const blockBack = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', blockBack);
    return () => window.removeEventListener('popstate', blockBack);
  }, []);

  const handleClose = () => {
    window.close();
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 100);
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!canSubmitFeedback || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`/api/screenings/${screeningId}/feedback`, {
        email: candidateEmail,
        stars,
        feedback: feedback.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {/* Header: same as other screening steps */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex flex-col">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {t('Powered by')}
            </p>
            <div className="mt-0.5 flex items-center gap-1">
              <img
                src="/logo.svg"
                alt="TalentCrux"
                className="h-14 w-14 object-contain flex-shrink-0"
              />
              <span className="text-2xl font-bold text-gray-900">TalentCrux</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
        </header>

        <div className="w-full rounded-2xl border border-gray-200 bg-white p-8 shadow-sm text-center">
          {searchParams.get('rejected') === 'true' ? (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                <svg className="h-8 w-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                {t('Screening Completed')}
              </h1>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
                {t('Thank you for your time and interest. Your screening process is now complete. We will review your profile and reach out if you are selected for the next stage.')}
              </p>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-lavender-purple-50">
                <CheckCircleIcon className="h-8 w-8 text-lavender-purple" aria-hidden />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                {t('Thank you for completing your screening.')}
              </h1>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                {t("We'll review your responses and follow up soon.")}
              </p>
            </>
          )}

          {/* Feedback section: only show when we have candidate email and not rejected */}
          {candidateEmail && searchParams.get('rejected') !== 'true' && (
            <div className="mt-8 pt-6 border-t border-gray-200 text-left">
              {submitted ? (
                <p className="text-sm font-medium text-lavender-purple">
                  {t('Thank you for your feedback.')}
                </p>
              ) : (
                <form onSubmit={handleSubmitFeedback} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('How would you rate your experience?')} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-1" role="group" aria-label={t('Star rating')}>
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setStars(value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setStars(value);
                            }
                          }}
                          className="p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2"
                          aria-label={value !== 1 ? t('{n} stars', { n: value }) : t('{n} star', { n: value })}
                          aria-pressed={stars >= value}
                        >
                          {stars >= value ? (
                            <StarIconSolid className="h-8 w-8 text-amber-400" />
                          ) : (
                            <StarIcon className="h-8 w-8 text-gray-300 hover:text-amber-200" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="feedback-text" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Your valuable feedback')}
                    </label>
                    <textarea
                      id="feedback-text"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-electric-blue focus:outline-none focus:ring-1 focus:ring-electric-blue"
                      placeholder={t('Optional: share any comments or suggestions...')}
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{t(error)}</p>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={!canSubmitFeedback || submitting}
                      className="inline-flex items-center justify-center rounded-lg bg-electric-blue px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-electric-blue-600 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                    >
                      {submitting ? t('Submitting…') : t('Submit')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
              {t('Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreeningThankYou;
